import React, { useState } from 'react';
import { Tabs, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FileTextOutlined,
  DollarOutlined,
  FormOutlined,
  SendOutlined,
  MessageOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';

// 导入所有SPO页面组件
import {
  SPOProcessPage,
  EngagementLetterPage,
  PaymentProofPage,
  IssuerSubmissionPage,
  FrameworkGeneratorPage,
  CommunicationRecordsPage,
  SPOResultPage,
} from '../../../spo';

interface SPOProcessProps {
  projectCode: string;
  userRole?: string;
  readonly?: boolean;
}

/**
 * SPO流程主组件
 *
 * 整合所有SPO相关功能页面：
 * 1. 流程概览
 * 2. Engagement Letter (委托协议)
 * 3. Payment Proof (付款凭证)
 * 4. Issuer Submission (发行人信息提交)
 * 5. Framework Generator (SPO框架生成)
 * 6. Communication Records (沟通记录)
 * 7. SPO Result (SPO结果)
 */
const SPOProcess: React.FC<SPOProcessProps> = ({
  projectCode,
  userRole = 'operator',
  readonly = false,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    {
      key: 'overview',
      label: (
        <span>
          <CheckCircleOutlined />{' '}
          {t('project:taskContent.spoProcess.tabs.overview')}
        </span>
      ),
      children: <SPOProcessPage projectId={projectCode} />,
      disabled: false,
    },
    {
      key: 'engagement-letter',
      label: (
        <span>
          <FileTextOutlined />{' '}
          {t('project:taskContent.spoProcess.tabs.engagementLetter')}
        </span>
      ),
      children: <EngagementLetterPage projectId={projectCode} />,
      disabled: false,
    },
    {
      key: 'payment-proof',
      label: (
        <span>
          <DollarOutlined />{' '}
          {t('project:taskContent.spoProcess.tabs.paymentProof')}
        </span>
      ),
      children: <PaymentProofPage projectId={projectCode} />,
      disabled: false,
    },
    {
      key: 'issuer-submission',
      label: (
        <span>
          <FormOutlined />{' '}
          {t('project:taskContent.spoProcess.tabs.issuerSubmission')}
        </span>
      ),
      children: <IssuerSubmissionPage projectId={projectCode} />,
      disabled: false,
    },
    {
      key: 'framework-generator',
      label: (
        <span>
          <SendOutlined />{' '}
          {t('project:taskContent.spoProcess.tabs.frameworkGenerator')}
        </span>
      ),
      children: <FrameworkGeneratorPage projectId={projectCode} />,
      disabled: false,
    },
    {
      key: 'communication',
      label: (
        <span>
          <MessageOutlined />{' '}
          {t('project:taskContent.spoProcess.tabs.communicationRecords')}
        </span>
      ),
      children: <CommunicationRecordsPage projectId={projectCode} />,
      disabled: false,
    },
    {
      key: 'result',
      label: (
        <span>
          <CheckCircleOutlined />{' '}
          {t('project:taskContent.spoProcess.tabs.result')}
        </span>
      ),
      children: <SPOResultPage projectId={projectCode} />,
      disabled: false,
    },
  ];

  return (
    <Card
      style={{
        minHeight: '600px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
      styles={{ body: { padding: '24px' } }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabs}
        type="card"
        size="large"
        tabBarStyle={{
          marginBottom: '24px',
          borderBottom: '2px solid #f0f0f0',
        }}
      />
    </Card>
  );
};

export default SPOProcess;
